// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, DollarSign, Target, Activity, Phone, Mail, Calendar, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

interface MetricCardProps {
  title: string;
  value: string | number;
  target?: number;
  icon: React.ElementType;
  trend?: string;
  trendDirection?: "up" | "down" | "neutral";
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  target, 
  icon: Icon, 
  trend, 
  trendDirection = "neutral" 
}) => {
  const trendColor = {
    up: "text-green-600",
    down: "text-red-600",
    neutral: "text-gray-600",
  };

  const progress = target ? (Number(value) / target) * 100 : 0;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {trend && (
              <p className={`text-sm ${trendColor[trendDirection]} flex items-center gap-1`}>
                <TrendingUp className="h-3 w-3" />
                {trend}
              </p>
            )}
          </div>
          <div className="p-3 bg-primary-50 rounded-lg">
            <Icon className="h-6 w-6 text-primary-600" />
          </div>
        </div>
        {target && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress to Goal</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const SalesPerformanceOverview: React.FC = () => {
  const [metrics, setMetrics] = useState({
    revenue: 0,
    deals: 0,
    activities: 0,
    pipeline: 0,
    calls: 0,
    emails: 0,
    meetings: 0,
    proposals: 0,
  });
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [pipelineData, setPipelineData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
    fetchRevenueData();
    fetchPipelineData();
  }, []);

  const fetchMetrics = async () => {
    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();

      // Fetch sales metrics for current month
      const { data: salesMetrics, error } = await supabase
        .from("sales_metrics")
        .select("*")
        .gte("metric_date", `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`);

      if (error) throw error;

      // Aggregate metrics
      const aggregated = salesMetrics?.reduce((acc, metric) => ({
        revenue: acc.revenue + Number(metric.revenue_closed || 0),
        deals: acc.deals + (metric.deals_closed || 0),
        pipeline: acc.pipeline + Number(metric.pipeline_value || 0),
        calls: acc.calls + (metric.calls_made || 0),
        emails: acc.emails + (metric.emails_sent || 0),
        meetings: acc.meetings + (metric.meetings_held || 0),
        proposals: acc.proposals + (metric.proposals_sent || 0),
      }), {
        revenue: 0,
        deals: 0,
        pipeline: 0,
        calls: 0,
        emails: 0,
        meetings: 0,
        proposals: 0,
      }) || metrics;

      setMetrics({
        ...aggregated,
        activities: aggregated.calls + aggregated.emails + aggregated.meetings,
      });
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueData = async () => {
    try {
      // Get revenue data from various sources for the last 6 months
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      // Fetch advertising campaigns revenue
      const { data: campaigns } = await supabase
        .from('advertising_campaigns')
        .select('total_budget, start_date, campaign_status')
        .gte('start_date', sixMonthsAgo.toISOString().split('T')[0])
        .eq('campaign_status', 'completed');

      // Fetch active partnerships revenue
      const { data: partnerships } = await supabase
        .from('active_partnerships')
        .select('monthly_revenue_generated, created_at')
        .gte('created_at', sixMonthsAgo.toISOString());

      // Create monthly revenue aggregation
      const monthlyRevenue: { [key: string]: number } = {};
      const months = [];
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthKey = date.toISOString().slice(0, 7); // YYYY-MM format
        monthlyRevenue[monthKey] = 0;
        months.push({
          month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          revenue: 0,
          monthKey
        });
      }

      // Add campaign revenue
      campaigns?.forEach(campaign => {
        const monthKey = campaign.start_date.slice(0, 7);
        if (monthlyRevenue.hasOwnProperty(monthKey)) {
          monthlyRevenue[monthKey] += campaign.total_budget || 0;
        }
      });

      // Add partnership revenue (monthly recurring)
      partnerships?.forEach(partnership => {
        const startMonth = partnership.created_at.slice(0, 7);
        Object.keys(monthlyRevenue).forEach(monthKey => {
          if (monthKey >= startMonth) {
            monthlyRevenue[monthKey] += partnership.monthly_revenue_generated || 0;
          }
        });
      });

      // Update months with actual revenue
      const revenueChartData = months.map(month => ({
        ...month,
        revenue: monthlyRevenue[month.monthKey]
      }));

      setRevenueData(revenueChartData);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    }
  };

  const fetchPipelineData = async () => {
    try {
      // Fetch pipeline data from all prospect sources
      const [advertiserResponse, partnerResponse, affiliateResponse] = await Promise.all([
        supabase.from('advertiser_prospects').select('pipeline_stage, estimated_campaign_value'),
        supabase.from('partner_prospects').select('pipeline_stage, partnership_value_potential'),
        supabase.from('affiliate_prospects').select('pipeline_stage, metadata')
      ]);

      const pipelineStages = {
        'prospect': { count: 0, value: 0, label: 'Prospects' },
        'qualification': { count: 0, value: 0, label: 'Qualification' },
        'proposal': { count: 0, value: 0, label: 'Proposal' },
        'negotiation': { count: 0, value: 0, label: 'Negotiation' },
        'agreement': { count: 0, value: 0, label: 'Agreement' },
        'closed': { count: 0, value: 0, label: 'Closed' }
      };

      // Process advertiser prospects
      advertiserResponse.data?.forEach(prospect => {
        const stage = prospect.pipeline_stage || 'prospect';
        if (pipelineStages[stage]) {
          pipelineStages[stage].count++;
          pipelineStages[stage].value += prospect.estimated_campaign_value || 0;
        }
      });

      // Process partner prospects
      partnerResponse.data?.forEach(prospect => {
        const stage = prospect.pipeline_stage || 'prospect';
        if (pipelineStages[stage]) {
          pipelineStages[stage].count++;
          pipelineStages[stage].value += prospect.partnership_value_potential || 0;
        }
      });

      // Process affiliate prospects (assume average value of $5000 per affiliate)
      affiliateResponse.data?.forEach(prospect => {
        const stage = prospect.pipeline_stage || 'prospect';
        if (pipelineStages[stage]) {
          pipelineStages[stage].count++;
          pipelineStages[stage].value += 5000; // Average affiliate value
        }
      });

      const pipelineChartData = Object.entries(pipelineStages).map(([key, data]) => ({
        stage: data.label,
        count: data.count,
        value: data.value,
        fill: getPipelineColor(key)
      }));

      setPipelineData(pipelineChartData);
    } catch (error) {
      console.error('Error fetching pipeline data:', error);
    }
  };

  const getPipelineColor = (stage: string) => {
    const colors: { [key: string]: string } = {
      'prospect': '#e5e7eb',
      'qualification': '#fbbf24',
      'proposal': '#60a5fa', 
      'negotiation': '#f97316',
      'agreement': '#10b981',
      'closed': '#059669'
    };
    return colors[stage] || '#6b7280';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-20"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Monthly Revenue"
          value={`$${metrics.revenue.toLocaleString()}`}
          target={50000}
          icon={DollarSign}
          trend="+12% vs last month"
          trendDirection="up"
        />
        <MetricCard
          title="Deals Closed"
          value={metrics.deals}
          target={25}
          icon={Target}
          trend="+3 vs last month"
          trendDirection="up"
        />
        <MetricCard
          title="Pipeline Value"
          value={`$${metrics.pipeline.toLocaleString()}`}
          icon={TrendingUp}
          trend="+8% vs last month"
          trendDirection="up"
        />
        <MetricCard
          title="Total Activities"
          value={metrics.activities}
          target={500}
          icon={Activity}
          trend="+15% vs last month"
          trendDirection="up"
        />
      </div>

      {/* Activity Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Calls Made"
          value={metrics.calls}
          target={150}
          icon={Phone}
          trend="+5% vs last month"
          trendDirection="up"
        />
        <MetricCard
          title="Emails Sent"
          value={metrics.emails}
          target={200}
          icon={Mail}
          trend="+18% vs last month"
          trendDirection="up"
        />
        <MetricCard
          title="Meetings Held"
          value={metrics.meetings}
          target={60}
          icon={Calendar}
          trend="+12% vs last month"
          trendDirection="up"
        />
        <MetricCard
          title="Proposals Sent"
          value={metrics.proposals}
          target={30}
          icon={FileText}
          trend="+7% vs last month"
          trendDirection="up"
        />
      </div>

      {/* Real Performance Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend (Last 6 Months)</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  tickMargin={5}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                  labelStyle={{ color: '#374151' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#2563eb" 
                  strokeWidth={3}
                  dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2, fill: '#fff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Pipeline Health</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height="60%">
                <PieChart>
                  <Pie
                    data={pipelineData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    label={({ stage, count }) => `${stage}: ${count}`}
                    labelLine={false}
                  >
                    {pipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, name) => [value, 'Prospects']} />
                </PieChart>
              </ResponsiveContainer>
              
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600">Pipeline Value by Stage:</p>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={pipelineData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="stage" tick={{ fontSize: 10 }} width={80} />
                    <Tooltip formatter={(value: number) => [formatCurrency(value), 'Value']} />
                    <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};